import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SystemNotification } from "@/store/notificationStore";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Check, 
  Trash2, 
  ArrowRight,
  Clock
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/currencyFormatter";
import { 
  getNotificationIconName, 
  getNotificationColorClass, 
  getNotificationBadgeColor 
} from "@/utils/notificationUtils";
import { 
  ShoppingBag, 
  Users, 
  MessageSquare, 
  Package, 
  Calendar,
  CreditCard,
  Settings,
  Shield,
  Mail,
  Bell,
  Megaphone,
  TrendingUp
} from "lucide-react";

interface NotificationCardProps {
  notification: SystemNotification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (notification: SystemNotification) => void;
}

const getNotificationIconComponent = (iconName: string) => {
  switch (iconName) {
    case 'shopping-bag':
      return <ShoppingBag className="h-5 w-5 text-orange-500" />;
    case 'users':
      return <Users className="h-5 w-5 text-blue-500" />;
    case 'message-square':
      return <MessageSquare className="h-5 w-5 text-green-500" />;
    case 'package':
      return <Package className="h-5 w-5 text-purple-500" />;
    case 'calendar':
      return <Calendar className="h-5 w-5 text-indigo-500" />;
    case 'credit-card':
      return <CreditCard className="h-5 w-5 text-emerald-500" />;
    case 'settings':
      return <Settings className="h-5 w-5 text-gray-500" />;
    case 'shield':
      return <Shield className="h-5 w-5 text-red-500" />;
    case 'mail':
      return <Mail className="h-5 w-5 text-cyan-500" />;
    case 'megaphone':
      return <Megaphone className="h-5 w-5 text-pink-500" />;
    case 'trending-up':
      return <TrendingUp className="h-5 w-5 text-teal-500" />;
    default:
      return <Bell className="h-5 w-5 text-gray-500" />;
  }
};

export const NotificationCard: React.FC<NotificationCardProps> = ({ 
  notification, 
  onMarkAsRead, 
  onDelete, 
  onNavigate 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleNavigate = () => {
    if (notification.entity_type === "order" && notification.entity_id) {
      navigate(`/dashboard/orders?orderId=${notification.entity_id}`);
    } else if (notification.entity_type === "user" && notification.entity_id) {
      navigate(`/dashboard/staff?userId=${notification.entity_id}`);
    } else if (notification.entity_type === "inventory" && notification.entity_id) {
      navigate(`/dashboard/inventory?itemId=${notification.entity_id}`);
    } else if (notification.entity_type === "reservation" && notification.entity_id) {
      navigate(`/dashboard/reservations?reservationId=${notification.entity_id}`);
    } else {
      onNavigate(notification);
    }
  };

  const iconName = getNotificationIconName(notification.type, notification.entity_type);
  const iconComponent = getNotificationIconComponent(iconName);

  return (
    <Card 
      className={cn(
        "transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-lg cursor-pointer",
        getNotificationColorClass(notification.type, notification.status)
      )}
      onClick={handleNavigate}
    >
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            {iconComponent}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">
                    {notification.title}
                  </h4>
                  <Badge 
                    variant="secondary" 
                    className={cn("text-xs", getNotificationBadgeColor(notification.type))}
                  >
                    {t(`notifications.types.${notification.type}`) || notification.type}
                  </Badge>
                  {notification.status === "unread" && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping" />
                  )}
                </div>
                
                <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                  {notification.message}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                  </span>
                  
                  <div className="flex items-center space-x-1">
                    {notification.status === "unread" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(notification.id);
                        }}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(notification.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                    
                    {notification.entity_type && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigate();
                        }}
                      >
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 